/**
 * Sessão do painel.
 *
 * Três caminhos de entrada, na ordem em que são tentados:
 *
 * 1. **SSO por webviewer** — o SGA Desk/Gestor abriu o painel em
 *    `/#/sso?token=…&refresh=…`. É o caminho de produção, o mesmo padrão do
 *    ConectDesk. O fragmento é limpo da URL logo após a leitura, para o token
 *    não sobrar no histórico da webview.
 * 2. **Refresh guardado** — recarregar a página não obriga a logar de novo.
 * 3. **Login local** — e-mail e senha, para a equipe de infra da SGA.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  api,
  aoPerderSessao,
  definirPersistencia,
  definirRefresh,
  definirTenant,
  definirToken,
  lembrarEmail,
  refreshGuardado,
} from './api';
import type { Sessao, Tenant, Usuario } from './types';

interface ContextoAuth {
  carregando: boolean;
  usuario: Usuario | null;
  tenants: Tenant[];
  tenantAtivo: Tenant | null;
  erro: string | null;
  entrar: (email: string, senha: string, manterConectado?: boolean) => Promise<void>;
  sair: () => Promise<void>;
  trocarTenant: (tenant: Tenant) => Promise<void>;
}

const Contexto = createContext<ContextoAuth | null>(null);

/** Lê `#/sso?token=…` e devolve os tokens, limpando a URL. */
function lerSsoDoFragmento(): { token: string; refresh: string; destino: string } | null {
  const hash = window.location.hash;
  if (!hash.startsWith('#/sso')) return null;

  const params = new URLSearchParams(hash.slice(hash.indexOf('?') + 1));
  const token = params.get('token');
  const refresh = params.get('refresh');
  if (!token) return null;

  // Remove o fragmento sem recarregar: o token sai da barra de endereço.
  window.history.replaceState(null, '', window.location.pathname);

  return { token, refresh: refresh ?? '', destino: params.get('destino') ?? '/' };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [carregando, setCarregando] = useState(true);
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantAtivo, setTenantAtivo] = useState<Tenant | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const aplicar = useCallback((sessao: Sessao) => {
    definirToken(sessao.token);
    definirRefresh(sessao.refresh_token);
    setUsuario(sessao.usuario);
    setTenants(sessao.tenants);

    const ativo = sessao.tenant ?? sessao.tenants[0] ?? null;
    setTenantAtivo(ativo);
    definirTenant(ativo?.slug ?? null);
    setErro(null);
  }, []);

  const limpar = useCallback(() => {
    definirToken(null);
    definirRefresh(null);
    definirTenant(null);
    setUsuario(null);
    setTenants([]);
    setTenantAtivo(null);
  }, []);

  // Bootstrap: SSO no fragmento, senão refresh guardado.
  useEffect(() => {
    let cancelado = false;

    (async () => {
      const sso = lerSsoDoFragmento();
      if (sso) {
        definirToken(sso.token);
        if (sso.refresh) definirRefresh(sso.refresh);
        try {
          const me = await api.get<{ usuario: Usuario; tenants: Tenant[]; tenant_ativo: string }>(
            '/auth/me',
          );
          if (cancelado) return;
          setUsuario(me.usuario);
          setTenants(me.tenants);
          const ativo = me.tenants.find((t) => t.id === me.tenant_ativo) ?? me.tenants[0] ?? null;
          setTenantAtivo(ativo);
          definirTenant(ativo?.slug ?? null);
        } catch {
          if (!cancelado) setErro('O link de acesso expirou. Abra o painel novamente pelo SGA.');
          limpar();
        }
        if (!cancelado) setCarregando(false);
        return;
      }

      if (refreshGuardado()) {
        const renovou = await api.renovarSessao();
        if (renovou && !cancelado) {
          try {
            const me = await api.get<{ usuario: Usuario; tenants: Tenant[]; tenant_ativo: string }>(
              '/auth/me',
            );
            setUsuario(me.usuario);
            setTenants(me.tenants);
            const ativo = me.tenants.find((t) => t.id === me.tenant_ativo) ?? me.tenants[0] ?? null;
            setTenantAtivo(ativo);
            definirTenant(ativo?.slug ?? null);
          } catch {
            limpar();
          }
        }
      }

      if (!cancelado) setCarregando(false);
    })();

    return () => {
      cancelado = true;
    };
  }, [limpar]);

  // Refresh esgotado no meio da navegação: volta para o login.
  useEffect(() => {
    aoPerderSessao(() => {
      limpar();
      setErro('Sua sessão expirou. Entre novamente.');
    });
  }, [limpar]);

  const entrar = useCallback(
    async (email: string, senha: string, manterConectado = false) => {
      // A persistência é decidida antes de guardar o token: `definirRefresh`
      // grava no store que a flag aponta.
      definirPersistencia(manterConectado);
      const sessao = await api.post<Sessao>('/auth/login', { email, senha });
      lembrarEmail(manterConectado ? email : null);
      aplicar(sessao);
    },
    [aplicar],
  );

  const sair = useCallback(async () => {
    await api.post('/auth/logout').catch(() => {});
    // Sair é explícito: derruba a persistência também, senão o próximo acesso
    // voltaria logado com um token que o usuário quis descartar.
    definirPersistencia(false);
    limpar();
  }, [limpar]);

  const trocarTenant = useCallback(
    async (tenant: Tenant) => {
      // O header já basta para as próximas chamadas; o refresh renova o token
      // com o `tenant_id` novo dentro do JWT, mantendo os dois lados coerentes.
      definirTenant(tenant.slug);
      setTenantAtivo(tenant);
      await api.renovarSessao();
    },
    [],
  );

  const valor = useMemo<ContextoAuth>(
    () => ({ carregando, usuario, tenants, tenantAtivo, erro, entrar, sair, trocarTenant }),
    [carregando, usuario, tenants, tenantAtivo, erro, entrar, sair, trocarTenant],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useAuth() {
  const ctx = useContext(Contexto);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}
